import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';

export type NotificationType = 
  | 'lead' 
  | 'todo' 
  | 'ticket' 
  | 'project' 
  | 'project_note' 
  | 'quote' 
  | 'inspection' 
  | 'calendar' 
  | 'location' 
  | 'battery';

export type NotificationChannel = 'inapp' | 'push';

export interface NotificationPreferences {
  id: string;
  user_id: string;
  lead_inapp: boolean;
  lead_push: boolean;
  todo_inapp: boolean;
  todo_push: boolean;
  ticket_inapp: boolean;
  ticket_push: boolean;
  project_inapp: boolean;
  project_push: boolean;
  project_note_inapp: boolean;
  project_note_push: boolean;
  quote_inapp: boolean;
  quote_push: boolean;
  inspection_inapp: boolean;
  inspection_push: boolean;
  calendar_inapp: boolean;
  calendar_push: boolean;
  location_inapp: boolean;
  location_push: boolean;
  battery_inapp: boolean;
  battery_push: boolean;
  quiet_hours_enabled: boolean;
  quiet_hours_start: string;
  quiet_hours_end: string;
  sound_enabled: boolean;
  vibration_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export const useNotificationPreferences = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Fetch preferences on mount
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    fetchPreferences();
  }, [user]);

  const fetchPreferences = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        // If no preferences exist, create default ones
        if (error.code === 'PGRST116') {
          await createDefaultPreferences();
        } else {
          throw error;
        }
      } else if (data) {
        setPreferences(data as NotificationPreferences);
      }
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      toast({
        title: 'Fout bij laden voorkeuren',
        description: 'Kon notificatie voorkeuren niet laden',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const createDefaultPreferences = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .insert({
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setPreferences(data as NotificationPreferences);
      }
    } catch (error) {
      console.error('Error creating default preferences:', error);
    }
  };

  const updatePreference = async (
    type: NotificationType,
    channel: NotificationChannel,
    enabled: boolean
  ) => {
    if (!user || !preferences) return;

    setUpdating(true);
    try {
      const key = `${type}_${channel}` as keyof NotificationPreferences;
      
      const { data, error } = await supabase
        .from('notification_preferences')
        .update({ [key]: enabled })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setPreferences(data as NotificationPreferences);
      }
    } catch (error) {
      console.error('Error updating preference:', error);
      toast({
        title: 'Fout bij opslaan',
        description: 'Kon voorkeur niet bijwerken',
        variant: 'destructive'
      });
    } finally {
      setUpdating(false);
    }
  };

  const toggleAll = async (channel: NotificationChannel, enabled: boolean) => {
    if (!user) return;

    setUpdating(true);
    try {
      const updates: Record<string, boolean> = {};
      const types: NotificationType[] = [
        'lead', 'todo', 'ticket', 'project', 'project_note',
        'quote', 'inspection', 'calendar', 'location', 'battery'
      ];
      
      types.forEach(type => {
        const key = `${type}_${channel}`;
        updates[key] = enabled;
      });

      const { data, error } = await supabase
        .from('notification_preferences')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setPreferences(data as NotificationPreferences);
        toast({
          title: enabled ? 'Alle notificaties ingeschakeld' : 'Alle notificaties uitgeschakeld',
          description: `${channel === 'inapp' ? 'In-app' : 'Push'} notificaties zijn ${enabled ? 'aan' : 'uit'}gezet`,
        });
      }
    } catch (error) {
      console.error('Error toggling all:', error);
      toast({
        title: 'Fout bij opslaan',
        description: 'Kon voorkeuren niet bijwerken',
        variant: 'destructive'
      });
    } finally {
      setUpdating(false);
    }
  };

  const updateGlobalSettings = async (settings: Partial<NotificationPreferences>) => {
    if (!user) return;

    setUpdating(true);
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .update(settings)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setPreferences(data as NotificationPreferences);
        toast({
          title: 'Instellingen opgeslagen',
          description: 'Je voorkeuren zijn bijgewerkt',
        });
      }
    } catch (error) {
      console.error('Error updating global settings:', error);
      toast({
        title: 'Fout bij opslaan',
        description: 'Kon instellingen niet bijwerken',
        variant: 'destructive'
      });
    } finally {
      setUpdating(false);
    }
  };

  // Check if notification should be sent based on preferences
  const shouldNotify = (type: NotificationType, channel: NotificationChannel): boolean => {
    if (!preferences) return true; // Default to allowing if no preferences set

    // Check quiet hours for push notifications
    if (channel === 'push' && preferences.quiet_hours_enabled) {
      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes();
      
      const [startHour, startMin] = preferences.quiet_hours_start.split(':').map(Number);
      const [endHour, endMin] = preferences.quiet_hours_end.split(':').map(Number);
      
      const startTime = startHour * 60 + startMin;
      const endTime = endHour * 60 + endMin;
      
      // Handle overnight quiet hours (e.g., 22:00 - 07:00)
      if (startTime > endTime) {
        if (currentTime >= startTime || currentTime < endTime) {
          return false; // In quiet hours
        }
      } else {
        if (currentTime >= startTime && currentTime < endTime) {
          return false; // In quiet hours
        }
      }
    }

    // Check specific preference
    const key = `${type}_${channel}` as keyof NotificationPreferences;
    return preferences[key] as boolean;
  };

  return {
    preferences,
    loading,
    updating,
    updatePreference,
    toggleAll,
    updateGlobalSettings,
    shouldNotify,
    refetch: fetchPreferences,
  };
};
